// Is this URL the public booking engine?
//
// Pure and separate from `BookingApp` so `main.tsx` can ask the question
// without importing the feature's stylesheet — the same arrangement
// `features/kiosk/entry.ts` has, and for the same reason: a false positive puts
// a guest surface in front of the front desk, and a false negative puts the
// front desk's tape chart in front of the internet.
//
// This is the THIRD guest surface, and the one that differs from the other two.
// A kiosk stands in a lobby and a tablet is in a room, so both are physically
// inside a building somebody owns. `/book` is reachable by everybody on the
// internet at once (docs/18 §1), which is why it branches above `AuthGate` and
// `AppShell` for a reason the other two do not have as well: bundle size is an
// exit criterion here (Lighthouse ≥ 90 on mobile, docs/11 §M8), and the staff
// shell is the largest thing in this build.
//
// THE MATCH IS ON THE PATH SEGMENT, NOT A PREFIX. `startsWith('/book')` says
// yes to `/booking` and `/bookings`, which are exactly the paths a staff
// reservations screen would take — so a prefix match would one day serve the
// public page to a clerk, or worse, hand a stranger a route somebody added
// assuming it was behind a session.

/** True for `/book` and anything under it — `/book/`, `/book/lookup`.
 *
 *  Sub-paths are allowed because the flow has two entrances a guest is given as
 *  a LINK: `/book` from an ad and `/book/lookup` from the confirmation email.
 *  A query parameter would be lost by every mail client that rewrites URLs. */
export function isBookingURL(pathname: string): boolean {
  const clean = pathname.replace(/\/+$/, '')
  return clean === '/book' || clean.startsWith('/book/')
}

/** Which entrance — the search flow, or "find my booking".
 *
 *  Read from the path rather than held in state, so the confirmation email's
 *  link lands the guest on the lookup form directly instead of on a search they
 *  did not ask for. */
export function bookingEntrance(pathname: string): 'search' | 'lookup' {
  const clean = pathname.replace(/\/+$/, '')
  return clean === '/book/lookup' ? 'lookup' : 'search'
}
